import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Plug, PlugsConnected, Trash, ArrowClockwise, LinkSimple, ArrowsClockwise, FloppyDisk } from "@phosphor-icons/react";
import { useAuth } from "@/lib/AuthContext";

const PROPOSAL_STATUSES = [
  { key: "draft", label: "Draft", hint: "New / rejected proposal" },
  { key: "pending_approval", label: "Pending Approval", hint: "Submitted, awaiting sign-off" },
  { key: "approved", label: "Approved", hint: "Approver signed off; ready to send" },
  { key: "issued", label: "Issued", hint: "Exported and sent to client" },
];

export default function IntegrationsPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pipelines, setPipelines] = useState([]);
  const [pipelinesBusy, setPipelinesBusy] = useState(false);
  const [pipelineId, setPipelineId] = useState("");
  const [mapping, setMapping] = useState({});
  const [savingMap, setSavingMap] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/hubspot");
      setStatus(data);
      if (data?.configured) {
        try {
          const mapRes = await api.get("/integrations/hubspot/stage_mapping");
          setMapping(mapRes.data?.stage_mapping || {});
          setPipelineId(mapRes.data?.stage_pipeline_id || "");
        } catch (e) { /* non-fatal */ }
      }
    } catch (e) { toast.error("Failed to load integration status"); }
  };

  const loadPipelines = async () => {
    setPipelinesBusy(true);
    try {
      const { data } = await api.get("/integrations/hubspot/pipelines");
      setPipelines(data?.pipelines || []);
      if (!pipelineId && data?.pipelines?.length) {
        setPipelineId(data.pipelines[0].id);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Failed to load pipelines"));
    } finally { setPipelinesBusy(false); }
  };

  const saveMapping = async () => {
    if (!pipelineId) { toast.error("Pick a pipeline first"); return; }
    setSavingMap(true);
    try {
      await api.put("/integrations/hubspot/stage_mapping", { stage_mapping: mapping, stage_pipeline_id: pipelineId });
      toast.success("Stage mapping saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Save failed"));
    } finally { setSavingMap(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!token || token.trim().length < 20) { toast.error("Paste a valid Private App token"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/integrations/hubspot", { access_token: token.trim() });
      setStatus(data);
      setToken("");
      toast.success("HubSpot connected");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Failed to save token"));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect HubSpot? Existing proposals will keep their linked IDs but new pushes will fail until you reconnect.")) return;
    setBusy(true);
    try {
      await api.delete("/integrations/hubspot");
      toast.info("Disconnected");
      load();
    } catch (e) { toast.error("Failed to disconnect"); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post("/integrations/hubspot/test");
      toast.success("Connection healthy");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Test failed"));
    } finally { setTesting(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl" data-testid="integrations-page">
      <div className="label-micro">Admin · Third-party</div>
      <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Integrations</h1>
      <p className="text-sm text-slate-600 mt-1">Connect TNAI Sales Suite to your other tools. Tokens are encrypted at rest.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm" data-testid="hubspot-card">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-100">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-sm bg-orange-100 flex items-center justify-center shrink-0">
              <Plug size={22} weight="fill" className="text-orange-600"/>
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-lg">HubSpot CRM</div>
              <div className="text-xs text-slate-500 mt-0.5">Pick contacts from your CRM, auto-create deals when proposals are exported, and attach the DOCX/PDF to the record's timeline.</div>
            </div>
          </div>
          {status?.configured ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-sm shrink-0" data-testid="hubspot-connected-badge">
              <CheckCircle size={12} weight="fill"/> Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 text-slate-700 px-2.5 py-1 rounded-sm shrink-0" data-testid="hubspot-disconnected-badge">
              Not connected
            </span>
          )}
        </div>

        <div className="p-5">
          {!isAdmin && (
            <div className="text-sm text-slate-600 border-l-2 border-amber-500 bg-amber-50 pl-3 py-2">Only admins can configure integrations.</div>
          )}

          {isAdmin && !status?.configured && (
            <div>
              <div className="label-micro">How to get a Private App token</div>
              <ol className="text-xs text-slate-600 mt-2 space-y-1 list-decimal ml-4">
                <li>Sign in to HubSpot as a Super Admin.</li>
                <li>Go to <b>Settings → Integrations → Private Apps</b> and click <b>Create private app</b>.</li>
                <li>Under <b>Scopes</b>, enable: <code className="text-[11px] bg-slate-100 px-1">crm.objects.contacts.read</code>, <code className="text-[11px] bg-slate-100 px-1">crm.objects.companies.read</code>, <code className="text-[11px] bg-slate-100 px-1">crm.objects.deals.read</code>, <code className="text-[11px] bg-slate-100 px-1">crm.objects.deals.write</code>, <code className="text-[11px] bg-slate-100 px-1">crm.objects.notes.write</code>, <code className="text-[11px] bg-slate-100 px-1">files</code>.</li>
                <li>Create the app, open the <b>Auth</b> tab, and copy the token.</li>
                <li>Paste it below and save.</li>
              </ol>
              <label className="block mt-4">
                <span className="label-micro">Private App access token</span>
                <input type="password" value={token} onChange={(e) => setToken(e.target.value)}
                       placeholder="pat-na1-… (starts with pat-)" data-testid="hubspot-token-input"
                       className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"/>
              </label>
              <p className="text-[11px] text-slate-500 mt-2">Never share this token — it grants full API access to your CRM. TNAI Sales Suite encrypts and stores it server-side only.</p>
              <div className="mt-4 flex gap-2">
                <button onClick={save} disabled={busy || !token} data-testid="hubspot-save-btn"
                        className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 inline-flex items-center gap-2 transition-colors duration-200 disabled:opacity-60">
                  <PlugsConnected size={14}/> {busy ? "Connecting…" : "Save & test connection"}
                </button>
              </div>
            </div>
          )}

          {isAdmin && status?.configured && (
            <div className="space-y-3" data-testid="hubspot-connected-state">
              <div className="text-sm">
                <div>Status: <b className="text-emerald-700">Active</b></div>
                <div className="text-xs text-slate-500 mt-1">Last updated {status?.updated_at ? new Date(status.updated_at).toLocaleString() : "—"}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={test} disabled={testing} data-testid="hubspot-test-btn" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors duration-200">
                  <ArrowClockwise size={12}/> {testing ? "Testing…" : "Test connection"}
                </button>
                <button onClick={disconnect} disabled={busy} data-testid="hubspot-disconnect-btn" className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-3 py-1.5 hover:bg-red-600 hover:text-white transition-colors duration-200">
                  <Trash size={12}/> Disconnect
                </button>
                <button onClick={() => { setStatus({ ...status, configured: false }); setToken(""); }} data-testid="hubspot-rotate-btn" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-100">
                  <LinkSimple size={12}/> Replace token
                </button>
              </div>
            </div>
          )}
        </div>

        {isAdmin && status?.configured && (
          <div className="border-t border-slate-100 p-5" data-testid="hubspot-stage-mapping-section">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="label-micro">Deal Stage Sync</div>
                <div className="text-sm text-slate-600 mt-0.5">Match Sales Suite proposal statuses to your HubSpot pipeline stages. Whenever a proposal moves, the linked HubSpot deal's stage updates automatically.</div>
              </div>
              <button onClick={loadPipelines} disabled={pipelinesBusy} data-testid="hs-load-pipelines"
                      className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-100 disabled:opacity-60 shrink-0">
                <ArrowsClockwise size={12} className={pipelinesBusy ? "animate-spin" : ""}/>
                {pipelines.length ? "Refresh pipelines" : (pipelinesBusy ? "Loading…" : "Load pipelines from HubSpot")}
              </button>
            </div>

            {pipelines.length > 0 && (
              <div className="space-y-4">
                <label className="block max-w-md">
                  <span className="label-micro">HubSpot Pipeline</span>
                  <select value={pipelineId} onChange={(e) => setPipelineId(e.target.value)} data-testid="hs-pipeline-select"
                          className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm">
                    {pipelines.map(p => <option key={p.id} value={p.id}>{p.label || p.id}</option>)}
                  </select>
                </label>

                {(() => {
                  const pipe = pipelines.find(p => p.id === pipelineId);
                  const stages = (pipe?.stages || []).slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
                  if (!stages.length) return <div className="text-xs text-slate-500">This pipeline has no stages configured.</div>;
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="hs-stage-mapping-grid">
                      {PROPOSAL_STATUSES.map(({ key, label, hint }) => (
                        <label key={key} className="block border border-slate-200 rounded-sm p-3 bg-slate-50">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-900">{label}</span>
                            <span className="label-micro">Sales Suite</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
                          <div className="mt-2">
                            <span className="label-micro">HubSpot stage</span>
                            <select value={mapping[key] || ""} data-testid={`hs-map-${key}`}
                                    onChange={(e) => setMapping({ ...mapping, [key]: e.target.value })}
                                    className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm bg-white">
                              <option value="">— None (skip sync) —</option>
                              {stages.map(s => <option key={s.id} value={s.id}>{s.label || s.id}</option>)}
                            </select>
                          </div>
                        </label>
                      ))}
                    </div>
                  );
                })()}

                <div className="flex items-center gap-2">
                  <button onClick={saveMapping} disabled={savingMap} data-testid="hs-save-mapping"
                          className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 inline-flex items-center gap-2 transition-colors duration-200 disabled:opacity-60">
                    <FloppyDisk size={14}/> {savingMap ? "Saving…" : "Save stage mapping"}
                  </button>
                  <span className="text-[11px] text-slate-500">Statuses left as "None" won't push a stage update to HubSpot.</span>
                </div>
              </div>
            )}
            {pipelines.length === 0 && !pipelinesBusy && (
              <div className="text-xs text-slate-500 border-l-2 border-slate-300 pl-3">Click "Load pipelines from HubSpot" to fetch your deal pipelines.</div>
            )}
          </div>
        )}
      </div>

      {/* Jira config card */}
      <JiraConfig isAdmin={isAdmin} />
    </div>
  );
}

function JiraConfig({ isAdmin }) {
  const [status, setStatus] = React.useState(null);
  const [siteUrl, setSiteUrl] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [apiToken, setApiToken] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [testing, setTesting] = React.useState(false);

  const load = async () => {
    try { const { data } = await api.get("/integrations/jira"); setStatus(data); }
    catch (e) { toast.error("Failed to load Jira status"); }
  };
  React.useEffect(() => { load(); }, []);

  const save = async () => {
    if (!siteUrl || !email || !apiToken) { toast.error("All Jira fields required"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/integrations/jira", { site_url: siteUrl.trim(), email: email.trim(), api_token: apiToken.trim() });
      setStatus(data); setSiteUrl(""); setEmail(""); setApiToken("");
      toast.success("Jira connected");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Failed to connect Jira"));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Jira? The Project summary section will stop loading data until you reconnect.")) return;
    setBusy(true);
    try { await api.delete("/integrations/jira"); toast.info("Disconnected"); load(); }
    catch (e) { toast.error("Failed to disconnect"); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true);
    try { const { data } = await api.post("/integrations/jira/test"); toast.success(`Connected as ${data.display_name}`); }
    catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Test failed"));
    } finally { setTesting(false); }
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-sm" data-testid="jira-card">
      <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Plug size={16} className="text-blue-600"/><h2 className="font-display font-bold text-lg">Jira Cloud</h2></div>
          <p className="text-sm text-slate-600 mt-1">Pull projects and issues from Atlassian Jira to power the Project summary dashboard.</p>
        </div>
        {status?.configured ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-sm shrink-0" data-testid="jira-connected-badge">
            <CheckCircle size={12} weight="fill"/> Connected
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 text-slate-700 px-2.5 py-1 rounded-sm shrink-0" data-testid="jira-disconnected-badge">Not connected</span>
        )}
      </div>
      <div className="p-5">
        {!isAdmin ? (
          <div className="text-sm text-slate-600 border-l-2 border-amber-500 bg-amber-50 pl-3 py-2">Only admins can configure integrations.</div>
        ) : !status?.configured ? (
          <div className="space-y-3">
            <label className="block"><span className="label-micro">Jira site URL</span>
              <input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://yourcompany.atlassian.net" data-testid="jira-site-input"
                     className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"/>
            </label>
            <label className="block"><span className="label-micro">Atlassian email</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="jira-email-input"
                     className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"/>
            </label>
            <label className="block"><span className="label-micro">API token</span>
              <input type="password" value={apiToken} onChange={(e) => setApiToken(e.target.value)}
                     placeholder="Paste API token" data-testid="jira-token-input"
                     className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm font-mono"/>
              <p className="text-[11px] text-slate-500 mt-1">Create one at <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">id.atlassian.com › Security › API tokens</a>. Stored encrypted at rest.</p>
            </label>
            <button onClick={save} disabled={busy} data-testid="jira-save-btn"
                    className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 inline-flex items-center gap-2 transition-colors duration-200 disabled:opacity-60">
              {busy ? "Connecting…" : "Connect Jira"}
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="jira-connected-state">
            <div className="text-sm text-slate-700"><b>Site:</b> {status.site_url}</div>
            <div className="text-sm text-slate-700"><b>Account:</b> {status.email}</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={test} disabled={testing} data-testid="jira-test-btn" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors">
                <ArrowClockwise size={12}/>{testing ? "Testing…" : "Test connection"}
              </button>
              <button onClick={disconnect} disabled={busy} data-testid="jira-disconnect-btn" className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-3 py-1.5 hover:bg-red-600 hover:text-white transition-colors">
                <Trash size={12}/>Disconnect
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
